import { suggestEcuCode } from "../../evaluation/ue/ecuCode";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  getAllClass,
  getAllClassroom,
  getAllSubject,
} from "../../../api/pedagogicElement";
import { getAllTeachers } from "../../../api/teachers";
import { Select } from "antd";
import { createCourse } from "../../../api/cours";
import { getAllUEByPeriod } from "../../../api/ue";
import { getAllEvaluationPeriodByLevel } from "../../../api/generalParam";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

interface Props {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface Subject {
  id: string;
  label: string;
}

interface Teacher {
  id: string;
  UserProfil: {
    firstName: string;
    lastName: string;
  };
}

export interface ClassTable {
  id: string;
  value: string;
  label: string;
  id_level?: string;
}

const CreateCoursModal = ({ show, onClose, onSaved }: Props) => {
  const { selectedYear } = useAcademicYear();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [allClass, setAllClass] = useState<ClassTable[]>([]);
  const [allClassroom, setAllClassroom] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<ClassTable[]>([]);
  const [classAssignments, setClassAssignments] = useState<Record<string, any>>({});
  const [uesByClass, setUesByClass] = useState<Record<string, any[]>>({});
  const [periodsByClass, setPeriodsByClass] = useState<Record<string, any[]>>({});

  // État pour stocker toutes les périodes d'évaluation
  // const [allPeriod, setAllPeriod] = useState<any[]>([]);

  // État pour stocker toutes les UEs
  // const [allUe, setAllUe] = useState<any[]>([]);

  // Schéma de validation du formulaire
  const formSchema = z.object({
    id_subject: z.string().min(1, { message: "La matière est requise" }),
    id_teacherProfil: z.string().min(1, { message: "L'enseignant est requis" }),
    status: z.string().min(1, { message: "L'état est requis" }),
    id_class: z.array(z.string()).min(1, { message: "La classe est requise" }),
    id_classroom: z.string().optional().nullable(),
    seance_number: z
      .number()
      .min(1, { message: "Le nombre de séances est requis" }),
  });

  // Type pour les données du formulaire
  type FormData = z.infer<typeof formSchema>;

  // Initialisation du formulaire
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id_subject: "",
      id_teacherProfil: "",
      status: "Non Approuvé", // Valeur par défaut
      id_class: [], // Valeur par défaut
      seance_number: 1, // Valeur par défaut
    },
  });

  const getModalContainer = () => {
    const modalElement = document.getElementById("add_cours_modal");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  // Fonction pour gérer la sélection des classes
  const handleClassChange = (
    selectedIds: string[],
    option?:
      | {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        }
      | {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        }[]
      | undefined,
  ) => {
    // option peut être undefined ou un tableau ou un objet, donc on doit gérer les cas
    let selectedOptions: {
      id: string;
      value: string;
      label: string;
      id_level?: string;
    }[] = [];
    if (Array.isArray(option)) {
      selectedOptions = option as {
        id: string;
        value: string;
        label: string;
        id_level?: string;
      }[];
    } else if (option) {
      selectedOptions = [
        option as {
          id: string;
          value: string;
          label: string;
          id_level?: string;
        },
      ];
    } else {
      // fallback: reconstruire à partir des valeurs sélectionnées
      selectedOptions = allClass.filter((classe) =>
        selectedIds.includes(classe.id),
      );
    }
    setSelectedClass(selectedOptions);
  };

  useEffect(() => {
    // console.log("Selected Class: ", selectedClass);
    setValue(
      "id_class",
      selectedClass.map((classe) => classe.id),
    );
  }, [selectedClass]);

  useEffect(() => {
    setClassAssignments((current) => {
      const next: Record<string, any> = {};
      selectedClass.forEach((classe) => {
        next[classe.id] = current[classe.id] || {
          id_class: classe.id,
          id_ue: null,
          id_evaluationPeriod: null,
          codeECU: "",
          credit: 1,
          coefficient: 1,
        };
      });
      return next;
    });
    Promise.all(
      selectedClass.map(async (classe) => {
        const response = await getAllEvaluationPeriodByLevel(
          classe.id_level,
          buildPermissionContext(Resource.COURSE, Action.CREATE),
        );
        return [classe.id, Array.isArray(response) ? response : response.data || []] as const;
      }),
    ).then((entries) => setPeriodsByClass(Object.fromEntries(entries))).catch(() => {
      toast.error("Impossible de charger les semestres des classes sélectionnées");
    });
  }, [selectedClass]);

  const updateAssignment = (classId: string, values: Record<string, any>) => {
    setClassAssignments((current) => ({
      ...current,
      [classId]: { ...current[classId], ...values },
    }));
  };

  const handlePeriodAssignmentChange = async (classId: string, periodId?: string) => {
    updateAssignment(classId, { id_evaluationPeriod: periodId || null, id_ue: null, codeECU: "" });
    if (!periodId) {
      setUesByClass((current) => ({ ...current, [classId]: [] }));
      return;
    }
    try {
      const response = await getAllUEByPeriod(classId, periodId, buildPermissionContext(Resource.COURSE, Action.CREATE));
      setUesByClass((current) => ({ ...current, [classId]: response.data || [] }));
    } catch {
      toast.error("Impossible de charger les UE de ce semestre");
    }
  };

  const handleUeAssignmentChange = (classId: string, ueId?: string) => {
    const ue = (uesByClass[classId] || []).find((item) => item.id === ueId);
    updateAssignment(classId, {
      id_ue: ueId || null,
      codeECU: ue ? suggestEcuCode(ue.codeUE, ue.SubjectUEs) : "",
    });
  };

  // Fonction pour récupérer les matières
  const fetchSubjects = async () => {
    try {
      if (!selectedYear?.id) return;
      const subjectsData = await getAllSubject(
        selectedYear.id,
        buildPermissionContext(Resource.COURSE, Action.CREATE),
      );
      const formattedSubjects = subjectsData.data.map((subject: any) => ({
        id: subject.id,
        label: subject.label,
      }));
      setSubjects(formattedSubjects);
    } catch (error: any) {
      // console.error("Erreur lors de la récupération des matières:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction pour récupérer les enseignants
  const fetchTeachers = async () => {
    try {
      const teachersData = await getAllTeachers(
        buildPermissionContext(Resource.COURSE, Action.CREATE),
      );
      const formattedTeachers = teachersData.data.map((teacher: any) => ({
        id: teacher.id,
        UserProfil: {
          firstName: teacher.firstname,
          lastName: teacher.lastname,
        },
      }));
      setTeachers(formattedTeachers);
    } catch (error: any) {
      // console.log("erreur récupération des enseignants ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour récupérer les classes
  const fetchAllClass = async () => {
    try {
      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.CREATE),
      );
      // console.log("Classes response: ", response);
      const formattedClass = response.data
        ?.slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((item: any) => ({
          id: item.id,
          value: item.id,
          label: `${item.Level.label} - ${item.label} `,
          id_level: item?.Level?.id,
        }));
      setAllClass(formattedClass || []);
    } catch (error: any) {
      // console.log("erreur de chargement des classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchAllClassroom = async () => {
    try {
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.CREATE),
      );
      const formattedClassroom = response.data.map((classroom: any) => ({
        id: classroom.id,
        value: classroom.id,
        label: classroom.label,
      }));
      setAllClassroom(formattedClassroom);
    } catch (error: any) {
      // console.log("erreur de chargement des salles : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Récupération des matières et des enseignants , classes
  useEffect(() => {
    if (!show && !selectedYear?.id) return;
    fetchSubjects();
    fetchTeachers();
    fetchAllClass();
    fetchAllClassroom();
  }, [show, selectedYear]);

  // Soumission du formulaire
  const onSubmit = async (data: FormData) => {
    if (!selectedYear?.id) {
      toast.error("Veuillez sélectionner une année académique");
      return;
    }

    const assignments = data.id_class.map((id) => classAssignments[id]);
    const invalidEcu = assignments.some(
      (item) => item?.id_ue && (!item.codeECU?.trim() || Number(item.credit) <= 0 || Number(item.coefficient) < 0),
    );
    if (invalidEcu) {
      toast.error("Renseignez le code ECU, les crédits et le coefficient pour chaque UE sélectionnée");
      return;
    }

    try {
      setIsLoading(true);

      // on crée le cours
      await createCourse(
        {
          state: data.status,
          id_subject: data.id_subject,
          id_teacherProfil: data.id_teacherProfil,
          number_session: data.seance_number,
          id_classroom: data.id_classroom || null,
          class_assignments: assignments,
        },
        buildPermissionContext(Resource.COURSE, Action.CREATE),
      );
      toast.success("Cours créé avec succès");
      onSaved(); // Rafraîchir la liste des cours
      handleClose(); // Fermer le modal
    } catch (error: any) {
      // console.error("Erreur lors de la création du cours:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite lors de la création du cours",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Réinitialisation du formulaire à la fermeture du modal
  const handleClose = () => {
    reset();
    setSelectedClass([]);
    setClassAssignments({});
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      backdrop="static"
      keyboard={false}
      // id="add_cours_modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Créer un nouveau cours</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row" id="add_cours_modal">
            {/* Matière */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Matière <span className="text-danger">*</span>
              </label>

              <Controller
                control={control}
                name="id_subject"
                render={({ field }) => (
                  <Select
                    showSearch
                    allowClear
                    className={
                      errors.id_subject ? "select is-invalid" : "select"
                    }
                    options={subjects.map((subject) => ({
                      value: subject.id,
                      label: subject.label,
                    }))}
                    placeholder="Sélectionner une matière"
                    getPopupContainer={getModalContainer}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                    {...field}
                    value={field.value || undefined}
                  />
                )}
              />
              {errors.id_subject && (
                <div className="invalid-feedback">
                  {errors.id_subject.message}
                </div>
              )}
            </div>

            {/* Enseignant */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Enseignant <span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                name="id_teacherProfil"
                render={({ field }) => (
                  <Select
                    showSearch
                    allowClear
                    className={
                      errors.id_teacherProfil ? "select is-invalid" : "select"
                    }
                    options={teachers.map((teacher) => ({
                      value: teacher.id,
                      label: `${teacher.UserProfil.firstName} ${teacher.UserProfil.lastName}`,
                    }))}
                    placeholder="Sélectionner un enseignant"
                    getPopupContainer={getModalContainer}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                    {...field}
                    value={field.value || undefined}
                  />
                )}
              />
              {errors.id_teacherProfil && (
                <div className="invalid-feedback">
                  {errors.id_teacherProfil.message}
                </div>
              )}
            </div>

            {/* État */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                État <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.status ? "is-invalid" : ""}`}
                {...register("status")}
                value={watch("status")}
              >
                <option value="Approuvé">Approuvé</option>
                <option value="Non Approuvé">Non approuvé</option>
              </select>
              {errors.status && (
                <div className="invalid-feedback">{errors.status.message}</div>
              )}
            </div>

            {/* Nombre de séances */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Nombre de séances <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={`form-control ${
                  errors.seance_number ? "is-invalid" : ""
                }`}
                {...register("seance_number", {
                  valueAsNumber: true,
                })}
              />
              {errors.seance_number && (
                <div className="invalid-feedback">
                  {errors.seance_number.message}
                </div>
              )}
            </div>

            {/* Classe */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Classe(s) <span className="text-danger">*</span>
              </label>

              <Select
                id="class-selector"
                mode="multiple"
                allowClear
                className="select"
                getPopupContainer={getModalContainer}
                style={{
                  width: "100%",
                }}
                placeholder="Seletionner une classe"
                defaultValue={[]}
                value={selectedClass.map((classe) => classe.id)}
                options={allClass}
                onChange={handleClassChange}
                // Permet de chercher par nom/prenom a lieu de l'id
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {errors.id_class && (
                <div className="invalid-feedback">
                  {errors.id_class.message}
                </div>
              )}
            </div>

            {/* Salle */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Salle</label>

              <Select
                id="classroom-selector"
                allowClear
                className="select"
                getPopupContainer={getModalContainer}
                style={{
                  width: "100%",
                }}
                placeholder="Seletionner une salle"
                defaultValue={undefined}
                // options={[]}
                options={allClassroom.map((classroom) => ({
                  value: classroom.id,
                  label: classroom.label,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => setValue("id_classroom", value)}
              />
              {errors.id_classroom && (
                <div className="invalid-feedback">
                  {errors.id_classroom.message}
                </div>
              )}
            </div>
            {selectedClass.length > 0 && (
              <div className="col-12 mt-2">
                <div className="border rounded-3 p-3 bg-light">
                  <h6 className="mb-1">Rattachement aux unités d’enseignement</h6>
                  <p className="text-muted small mb-3">Choisissez, pour chaque classe, l’UE du cours. Laissez « Sans UE » si le rattachement n’est pas encore défini.</p>
                  {selectedClass.map((classe) => {
                    const assignment = classAssignments[classe.id] || {};
                    return (
                      <div className="bg-white border rounded-2 p-3 mb-2" key={classe.id}>
                        <div className="fw-semibold mb-2">{classe.label}</div>
                        <div className="d-grid gap-2 overflow-auto" style={{ gridTemplateColumns: "repeat(3, minmax(170px, 1fr)) repeat(2, minmax(90px, .55fr))" }}>
                          <div><label className="form-label small mb-1">Semestre</label>
                            <Select allowClear showSearch style={{ width: "100%" }} placeholder="Semestre"
                              value={assignment.id_evaluationPeriod || undefined}
                              options={(periodsByClass[classe.id] || []).map((period) => ({ value: period.id, label: period.label }))}
                              onChange={(value) => handlePeriodAssignmentChange(classe.id, value)}
                              getPopupContainer={() => document.body} dropdownStyle={{ zIndex: 2000 }} />
                          </div>
                          <div><label className="form-label small mb-1">UE</label>
                            <Select allowClear showSearch style={{ width: "100%" }} placeholder="Sans UE" disabled={!assignment.id_evaluationPeriod}
                              value={assignment.id_ue || undefined}
                              options={(uesByClass[classe.id] || []).map((ue) => ({ value: ue.id, label: `${ue.codeUE} - ${ue.label}` }))}
                              onChange={(value) => handleUeAssignmentChange(classe.id, value)}
                              getPopupContainer={() => document.body} dropdownStyle={{ zIndex: 2000 }}
                              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                          </div>
                          <div><label className="form-label small mb-1">Code ECU</label><input className="form-control" placeholder="Ex. 1 MGT2440" disabled={!assignment.id_ue} value={assignment.codeECU || ""} onChange={(e) => updateAssignment(classe.id, { codeECU: e.target.value })} /></div>
                          <div><label className="form-label small mb-1">Créd.</label><input className="form-control" type="number" min="1" placeholder="Créd." disabled={!assignment.id_ue} value={assignment.credit ?? 1} onChange={(e) => updateAssignment(classe.id, { credit: Number(e.target.value) })} /></div>
                          <div><label className="form-label small mb-1">Coeff.</label><input className="form-control" type="number" min="0" placeholder="Coeff." disabled={!assignment.id_ue} value={assignment.coefficient ?? 1} onChange={(e) => updateAssignment(classe.id, { coefficient: Number(e.target.value) })} /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleClose}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Création...
                </>
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default withPermission(
  CreateCoursModal,
  [buildPermission(Resource.COURSE, Action.CREATE)],
  "masquer",
);
